import { isLocale, parseLocale } from 'shared/locales'
import type { AppProps } from 'shared/types'
import { getCookie } from '@/lib/cookies'
import { clientRadarStyle, persistRadarStyle } from '@/lib/radar-style'
import type { RadarStyle } from '@/lib/radar-style'
import { create } from 'zustand'

export const useAppStore = create<{
  store: AppProps
  // Partial: setStore deep-merges, so callers can update just accentColor/darkMode/button.
  setStore: (value: Partial<Omit<AppProps, 'customize'>> & { customize?: Partial<AppProps['customize']> }) => void
  newVersionAvailable: boolean
  setNewVersionAvailable: (value: boolean) => void
  // Read it via useHydrated() to gate SSR'd controls, so pre-hydration clicks aren't silently dropped.
  hydrated: boolean
  setHydrated: () => void
  // Super-admin preview switch, kept in a cookie so the SERVER renders the chosen drawing too (see radar-style.ts).
  // This is the live value the toggle moves; components read it through useRadarStyle(), which is seeded from the
  // root loader so SSR and the first client render agree.
  radarStyle: RadarStyle
  setRadarStyle: (value: RadarStyle) => void
  // Latch: the gate only shows while unanswered, so without it the registration step count would shift.
  peopleStepShown: boolean
  markPeopleStepShown: () => void
  // In-memory only: "Close" must reopen the guide on the next entry; only "Don't show again" persists (tour=1).
  // Both latches hold the user who answered, since closing a session is a client navigation that keeps this store.
  welcomeDismissedBy: number | null
  dismissWelcome: (userId: number) => void
  resetWelcome: () => void
  // ACCEPT on the sample notice, ahead of the session refetch that clears `sampleNotice` for good.
  sampleNoticeDismissedBy: number | null
  dismissSampleNotice: (userId: number) => void
}>((set) => ({
  store: {
    sdk: false,
    app: undefined,
    clientId:
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('clientId') ?? undefined)
        : undefined,
    userToken:
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('token') ?? undefined)
        : undefined,
    extension: undefined,
    callback: () => undefined,
    routeTo: undefined,
    applicantData: undefined,
    companyData: undefined,
    jobData: undefined,
    profiles: undefined,
    customize: {
      // null = no live SDK override, so a reload paints the accent from the suspense user and a postMessage still wins.
      accentColor: null,
      primaryFont: "'Plus Jakarta Sans', fallback-font, sans-serif",
      secondaryFont: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      taskColor: '#0bd',
      peopleColor: '#9c5',
      contextColor: '#fc4',
      compareColor: '#f60',
      positioningColor: '#4b9faa',
      primaryErrorColor: '#F02849',
      secondaryErrorColor: '#EC5990',
      tourColor: '#357cf0',
      doneColor: '#25C133',
      // From SDK
      disableLogo: false,
      disableReportDescription: false,
      defaultReportPage: 'detailed',
      loginUrl: undefined,
      // accentColor/darkMode/button fields come from user.app via the AppShell sync (see above).
      darkMode: undefined,
      applicantButtonExit: undefined,
      jobButtonExit: undefined,
      enableJobAddApplicantButton: true,
    },
    // A stale `theme=undefined` cookie would otherwise read back as the string "undefined" and count as an explicit mode.
    darkMode: ((t) => (t === 'dark' || t === 'light' ? t : 'system'))(getCookie('theme')),
    resolvedDarkMode: 'light',
    // Must match the locale the server already rendered, or every translated node mismatches at hydration (#418).
    locale: (() => {
      if (typeof window === 'undefined') {
        const cookieLang = getCookie('language')
        return isLocale(cookieLang) ? cookieLang : parseLocale({})
      }
      const serverLang = document.documentElement.lang
      if (isLocale(serverLang)) return serverLang
      const langParam = new URLSearchParams(window.location.search).get('lang')
      if (isLocale(langParam)) return langParam
      const cookieLang = getCookie('language')
      if (isLocale(cookieLang)) return cookieLang
      return parseLocale({ 'accept-language': navigator.language })
    })(),
  },
  setStore: (store) =>
    set((state) => ({
      store: {
        ...state.store,
        ...store,
        customize: {
          ...state.store.customize,
          ...store.customize,
        },
      },
    })),
  newVersionAvailable: false,
  setNewVersionAvailable: (newVersionAvailable) => set({ newVersionAvailable }),
  hydrated: false,
  setHydrated: () => set({ hydrated: true }),
  // 'classic' during SSR (no document), which is fine: the server's value comes from the root loader, and on the
  // client this reads the same cookie the server did, so hydration matches.
  radarStyle: clientRadarStyle(),
  setRadarStyle: (radarStyle) => {
    persistRadarStyle(radarStyle)
    set({ radarStyle })
  },
  peopleStepShown: false,
  markPeopleStepShown: () => set({ peopleStepShown: true }),
  // Unset on load: a reload re-arms the guide, and it is only read after hydration, so SSR cannot mismatch.
  welcomeDismissedBy: null,
  dismissWelcome: (userId) => set({ welcomeDismissedBy: userId }),
  resetWelcome: () => set({ welcomeDismissedBy: null }),
  sampleNoticeDismissedBy: null,
  dismissSampleNotice: (userId) => set({ sampleNoticeDismissedBy: userId }),
}))
